import type { MouseEventHandler, ReactNode } from 'react';

import { Bot, Building2, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';

import { useEmail } from '@/components/providers/EmailProvider/EmailProvider';
import useSelectedParty from '@/features/analysis/hooks/useSelectedParty';
import { getPartyName } from '@/lib/party';
import cn from '@/lib/utils/cn';

const Tab = ({
  label,
  icon,
  to,
  disabled,
}: {
  label: string;
  icon: ReactNode;
  to: string;
  disabled?: boolean;
}) => {
  const handleClick: MouseEventHandler = (e) => {
    if (!disabled) return;

    e.preventDefault();
  };

  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'flex items-center justify-center gap-1 rounded p-2 text-xs font-bold transition-colors',
          {
            ['bg-primary-500 text-primary-50 hover:bg-primary-500 hover:text-primary-50']:
              isActive,
            ['hover:bg-primary-50 hover:text-primary-600 dark:hover:text-primary-500 dark:hover:bg-primary-800/50']:
              !isActive,
            ['hover:text-muted-foreground/50 text-muted-foreground/50 cursor-not-allowed bg-transparent hover:bg-transparent']:
              disabled,
          },
        )
      }
      aria-disabled={disabled}
      onClick={handleClick}
    >
      <span className="shrink-0">{icon}</span>
      <span className="truncate">{label}</span>
    </NavLink>
  );
};

export default function MainTabs() {
  const { t } = useTranslation();
  const party = useSelectedParty();
  const { isPartySelected } = useEmail();

  const partyName = party && getPartyName(party);

  return (
    <div className="grid w-full grid-cols-2 items-center gap-1 border-b p-1">
      <Tab
        label={t('SIDEBAR_TAB_ASSISTANT')}
        icon={<Bot className="size-3.5" />}
        to="/assistant"
      />

      <span data-onboarding-id="party-tab">
        <Tab
          label={
            party && partyName ? partyName : t('SIDEBAR_PARTY_PLACEHOLDER')
          }
          icon={
            party?.type === 'CUSTOMER' ? (
              <User className="size-3.5" />
            ) : (
              <Building2 className="size-3.5" />
            )
          }
          to="/party"
          disabled={!isPartySelected}
        />
      </span>
    </div>
  );
}
